/** @format */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Button from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { exportToCSV } from '../../utils/csvExport';
import { getClientDetailsUrl } from '../../constants/routes';
import { 
	FiX, 
	FiCalendar, 
	FiUser, 
	FiDollarSign, 
	FiExternalLink, 
	FiDownload,
	FiAlertTriangle,
	FiClock
} from 'react-icons/fi';

// Constants for renewal urgency thresholds
const URGENCY_THRESHOLDS = {
	CRITICAL: 3, // days
	URGENT: 7,   // days
	UPCOMING: 30 // days
};

const RenewalManagementModal = ({ 
	isOpen, 
	onClose, 
	urgentRenewalsList = [], 
	upcomingRenewalsList = [] 
}) => {
	const [showUpcoming, setShowUpcoming] = useState(false);
	const modalRef = useRef(null);
	const closeButtonRef = useRef(null);

	// Handle keyboard navigation and focus management
	useEffect(() => {
		if (isOpen) {
			// Focus the close button when modal opens
			setTimeout(() => closeButtonRef.current?.focus(), 100);
			
			// Handle escape key
			const handleEscape = (e) => {
				if (e.key === 'Escape') {
					onClose();
				}
			};
			
			document.addEventListener('keydown', handleEscape);
			return () => document.removeEventListener('keydown', handleEscape);
		}
	}, [isOpen, onClose]);

	// Format currency display
	const formatCurrency = (amount) => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount / 100); // Convert from cents
	};

	// Format date display
	const formatDate = (date) => {
		return date.toLocaleDateString('en-US', { 
			month: 'short', 
			day: 'numeric',
			year: 'numeric'
		});
	};

	// Get urgency styling aligned with Jane Rothe design system
	const getUrgencyBadge = (daysUntilRenewal) => {
		if (daysUntilRenewal <= URGENCY_THRESHOLDS.CRITICAL) {
			return <Badge variant="outline" className="text-xs border-primary text-primary font-montserrat-medium">Critical</Badge>;
		} else if (daysUntilRenewal <= URGENCY_THRESHOLDS.URGENT) {
			return <Badge variant="outline" className="text-xs border-clientBlack text-clientBlack font-montserrat-medium">Urgent</Badge>;
		} else {
			return <Badge variant="outline" className="text-xs border-gray-400 text-gray-600 font-montserrat-medium">Upcoming</Badge>;
		}
	};

	// Handle export functionality using reusable CSV utility
	const handleExport = () => {
		const allRenewals = [...urgentRenewalsList, ...upcomingRenewalsList];
		
		const headers = [
			{ key: 'customerName', label: 'Customer Name' },
			{ key: 'planName', label: 'Plan Name' },
			{ key: 'formattedAmount', label: 'Amount' },
			{ key: 'formattedDate', label: 'Renewal Date' },
			{ key: 'daysUntilRenewal', label: 'Days Until Renewal' },
			{ key: 'status', label: 'Status' }
		];
		
		// Transform data to include formatted values
		const exportData = allRenewals.map(renewal => ({
			...renewal,
			formattedAmount: formatCurrency(renewal.amount),
			formattedDate: formatDate(renewal.renewalDate)
		}));
		
		const filename = `renewals-${new Date().toISOString().split('T')[0]}`;
		exportToCSV(exportData, headers, filename);
	};


	// Render renewal item aligned with Jane Rothe design system
	const RenewalItem = ({ renewal, isUrgent = false }) => (
		<div className={`p-4 border rounded-sm ${
			isUrgent 
				? 'border-2 border-clientBlack bg-gray-50 shadow-sm' 
				: 'border border-gray-200 bg-white'
		} hover:shadow-md transition-all duration-200`}>
			<div className="flex justify-between items-start mb-3">
				<div className="flex-1">
					<div className="flex items-center gap-3 mb-2">
						<h4 className="font-glacial text-lg text-clientNero">{renewal.customerName}</h4>
						{getUrgencyBadge(renewal.daysUntilRenewal)}
					</div>
					<p className="text-sm font-montserrat-medium text-gray-600 mb-1">{renewal.planName}</p>
					<div className="flex items-center gap-4 text-xs text-gray-500">
						<span className="flex items-center gap-1">
							<FiDollarSign className="h-3 w-3" />
							{formatCurrency(renewal.amount)}
						</span>
						<span className="flex items-center gap-1">
							<FiCalendar className="h-3 w-3" />
							{formatDate(renewal.renewalDate)}
						</span>
						<span className="flex items-center gap-1">
							<FiClock className="h-3 w-3" />
							{renewal.daysUntilRenewal} days
						</span>
					</div>
				</div>
			</div>
			
			<div className="flex gap-2">
				<Button 
					variant="ghost" 
					size="sm" 
					className="text-xs"
					onClick={() => window.open(getClientDetailsUrl(renewal.username), '_blank')}
				>
					<FiExternalLink className="h-3 w-3 mr-1" />
					View Details
				</Button>
			</div>
		</div>
	);

	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			{/* Backdrop */}
			<div 
				className="absolute inset-0 bg-black bg-opacity-50" 
				onClick={onClose}
			/>
			
			{/* Modal */}
			<div 
				ref={modalRef}
				className="relative w-full max-w-4xl max-h-[90vh] mx-4 bg-white rounded-lg shadow-xl overflow-hidden"
				role="dialog"
				aria-modal="true"
				aria-labelledby="renewal-modal-title"
				aria-describedby="renewal-modal-description"
			>
				{/* Header */}
				<div className="flex items-center justify-between p-6 border-b border-gray-200">
					<div className="flex items-center gap-3">
						<FiCalendar className="h-6 w-6 text-primary" />
						<div>
							<h2 id="renewal-modal-title" className="text-xl font-glacial text-gray-900">Upcoming Renewals</h2>
							<p id="renewal-modal-description" className="text-sm font-montserrat-regular text-gray-600">
								Next 30 days • {urgentRenewalsList.length + upcomingRenewalsList.length} total renewals
							</p>
						</div>
					</div>
					<div className="flex items-center gap-2">
						<Button 
							variant="outline" 
							size="sm"
							onClick={handleExport}
							className="text-xs"
						>
							<FiDownload className="h-4 w-4 mr-1" />
							Export
						</Button>
						<Button 
							ref={closeButtonRef}
							variant="ghost" 
							size="sm"
							onClick={onClose}
							aria-label="Close renewal management modal"
						>
							<FiX className="h-5 w-5" />
						</Button>
					</div>
				</div>

				{/* Content */}
				<div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
					{/* Critical Renewals Section - Jane Rothe Design System */}
					{urgentRenewalsList.length > 0 && (
						<div className="mb-8">
							<div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-sm border-l-4 border-primary">
								<FiAlertTriangle className="h-6 w-6 text-primary" />
								<h3 className="text-xl font-glacial text-clientNero">
									Critical Renewals (Next 7 Days)
								</h3>
								<Badge variant="outline" className="ml-2 border-primary text-primary font-montserrat-medium">
									{urgentRenewalsList.length}
								</Badge>
							</div>
							<div className="space-y-3">
								{urgentRenewalsList.map((renewal) => (
									<RenewalItem 
										key={renewal.subscriptionId} 
										renewal={renewal} 
										isUrgent={true}
									/>
								))}
							</div>
						</div>
					)}

					{/* Upcoming Renewals Section - Jane Rothe Design System */}
					{upcomingRenewalsList.length > 0 && (
						<div>
							<div className="flex items-center justify-between mb-4">
								<div className="flex items-center gap-3 p-3 bg-white rounded-sm border-l-4 border-gray-300">
									<FiClock className="h-6 w-6 text-clientBlack" />
									<h3 className="text-xl font-glacial text-clientNero">
										Upcoming Renewals (8-30 Days)
									</h3>
									<Badge variant="outline" className="ml-2 border-gray-400 text-gray-600 font-montserrat-medium">
										{upcomingRenewalsList.length}
									</Badge>
								</div>
								{upcomingRenewalsList.length > 5 && (
									<Button 
										variant="ghost" 
										size="sm"
										onClick={() => setShowUpcoming(!showUpcoming)}
										className="text-xs"
									>
										{showUpcoming ? 'Show Less' : 'Show All'}
									</Button>
								)}
							</div>
							<div className="space-y-3">
								{(showUpcoming ? upcomingRenewalsList : upcomingRenewalsList.slice(0, 5)).map((renewal) => (
									<RenewalItem 
										key={renewal.subscriptionId} 
										renewal={renewal} 
										isUrgent={false}
									/>
								))}
							</div>
						</div>
					)}

					{/* Empty State */}
					{urgentRenewalsList.length === 0 && upcomingRenewalsList.length === 0 && (
						<div className="text-center py-12">
							<FiCalendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
							<h3 className="text-lg font-montserrat-medium text-gray-900 mb-2">
								No Upcoming Renewals
							</h3>
							<p className="text-sm font-montserrat-regular text-gray-600">
								All subscriptions are current with no renewals in the next 30 days.
							</p>
						</div>
					)}
				</div>
			</div>
		</div>,
		document.body
	);
};

export default RenewalManagementModal;